<script setup>
/**
 * Este componente gestiona lo que es el modal en sí, el layout del modal, cargar el
 * contenido y ponerlo etc. No hace nada del modal, ni abrir ni cerrar ni nada, eso
 * se debe de hacer desde otro componente, este solo gestiona lo que es el contenido
 *
 * Se trata del layout de un modal de bootstrap
 */
import axios from 'axios';
import {
  nextTick, onMounted, ref, useTemplateRef, watch,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import ModalBootstrapLayout from './ModalBootstrapLayout.vue';

const props = defineProps({
  id: { type: String, required: true },
  url: { type: String, required: true },
  // Rails para entender que tiene que responder un js o un partial son decorar
  // con todo el layout de la aplicación hay que enviarle accept: text/javascript
  forzarPeticionJs: { type: Boolean, default: true },
});

const emits = defineEmits(['prepararPeticion', 'htmlActualizado', 'ocultarModal', 'cancelado']);

const contenidoWrapper = useTemplateRef('contenido-wrapper');

const cargando = ref(false);
const contenido = ref(null);

function setHtml(html) {
  contenido.value = html;

  // Next tick para que el contenido ya esté accesible
  nextTick(() => {
    // Si están limpiando o han indicado a cancelar, no existe el wrapper
    if (contenidoWrapper.value) {
      // Esto son las X de cerrar el modal antiguo. Así todo nos valdría ahora mismo
      // también si queremos poner un botón de cerrar o lo que sea
      contenidoWrapper.value.querySelectorAll('[data-dismiss="modal"]').forEach((el) => {
        el.addEventListener('click', () => {
          emits('ocultarModal');
        });
      });
    }
    emits('htmlActualizado', contenido.value);
  });
}

function cargarUrl(urlInicial) {
  cargando.value = true;
  let url = urlInicial;
  const opts = {};

  const datosEvento = { url, cancelar: false };
  emits('prepararPeticion', datosEvento);

  url = datosEvento.url;

  // Si no devuelven URL es que cancelan la petición
  if (datosEvento.cancelar || !url) {
    emits('cancelado');
    return;
  }

  if (props.forzarPeticionJs) {
    opts.headers = {
      accept: 'text/javascript',
    };
  }

  axios.get(url, opts).then((result) => {
    setHtml(result.data);
  }).catch((result) => {
    setHtml(result.response?.data);
    // TODO: Lanzar evento?
  }).finally(() => {
    cargando.value = false;
  });
}

function cargarHtmlDesdeUrl(refUrl) {
  // Primero limpiamos
  setHtml('');
  cargarUrl(refUrl);
}

watch(() => props.url, () => {
  cargarHtmlDesdeUrl(props.url);
});

onMounted(() => {
  cargarHtmlDesdeUrl(props.url);
});

// ******* Conectamos a turbo-stream para responder a eventos
// useEventListener se encarga de conectar el evento y desconectarlo en el unmount
useEventListener(document, 'turbo:before-stream-render', (event) => {
  if (!event.target) {
    return;
  }
  if (event.target.getAttribute('action') === 'hide_modal') {
    emits('ocultarModal');
  }
});

// ****************** Funciones públicas
defineExpose({ cargarHtmlDesdeUrl });
</script>

<template>
  <ModalBootstrapLayout
    :id="id"
    :cargando="cargando"
  >
    <div v-html="contenido"></div>
  </ModalBootstrapLayout>
</template>
